import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { Clock, AlertTriangle, ChevronDown, ChevronUp, User } from 'lucide-react';
import { Button } from './ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';

/**
 * RestPeriodViolationsList - Zeigt alle Ruhezeitverletzungen eines Zeitraums
 * Für Admin/Vorgesetzte zur Compliance-Übersicht
 * 
 * Props:
 * - userId: Optional - Filter für bestimmten Benutzer
 * - startDate: Start des Zeitraums (YYYY-MM-DD)
 * - endDate: Ende des Zeitraums (YYYY-MM-DD)
 */
export default function RestPeriodViolationsList({ userId, startDate, endDate }) {
  const { api, user } = useAuth();
  const [violations, setViolations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  // Format date for display
  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEE, d. MMM yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  // Default to current week if no dates provided
  const getDefaultDates = () => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    return {
      start: format(weekStart, 'yyyy-MM-dd'),
      end: format(weekEnd, 'yyyy-MM-dd')
    };
  };

  const fetchViolations = useCallback(async () => {
    setIsLoading(true);
    try {
      const dates = startDate && endDate 
        ? { start: startDate, end: endDate }
        : getDefaultDates();
      
      const params = new URLSearchParams({
        start_date: dates.start,
        end_date: dates.end
      });
      if (userId) {
        params.append('user_id', userId);
      }
      
      const response = await api.get(`/rest-period-violations?${params.toString()}`);
      setViolations(response.data.violations || []);
    } catch (error) {
      console.error('Fehler beim Laden der Ruhezeitverletzungen:', error);
      setViolations([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, userId, startDate, endDate]);

  useEffect(() => {
    // Only fetch for admin/supervisor
    if (user?.role === 'admin' || user?.role === 'supervisor') {
      fetchViolations();
    }
  }, [fetchViolations, user?.role]);

  // Don't show for regular employees
  if (user?.role !== 'admin' && user?.role !== 'supervisor') {
    return null;
  }

  if (isLoading) {
    return (
      <div className="text-muted-foreground text-sm">Lade Ruhezeitverletzungen...</div>
    );
  }

  if (violations.length === 0) {
    return null; // Don't show if no violations
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between text-amber-400 hover:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3"
          data-testid="rest-violations-toggle"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span className="font-medium">
              {violations.length} Ruhezeitverletzung{violations.length !== 1 ? 'en' : ''} (§ 5 ArbZG)
            </span>
          </div>
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="bg-muted/50 rounded-lg border border-border divide-y divide-zinc-700" data-testid="rest-violations-list">
          {violations.map((v, idx) => (
            <div key={idx} className="p-3 text-sm">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-foreground font-medium">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  {formatDate(v.date)}
                </div>
                <span className="text-red-600 dark:text-red-400 font-mono">
                  {v.actual_hours?.toFixed(1)}h / {v.required_hours}h
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                Ende Vortag: {v.prev_end_time} • Start: {v.curr_start_time}
                <span className="text-red-600 dark:text-red-400 ml-2">
                  (−{v.shortage_hours?.toFixed(1)}h fehlen)
                </span>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
